import React from 'react';
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  pdf,
} from '@react-pdf/renderer';
import type { ConsignmentReconciliationResponse } from '../types/venue';

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmtDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const fmtDateTime = (iso: string): string =>
  new Date(iso).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const fmtSize = (v?: number | null, u?: string | null): string => {
  if (!v) return '';
  return u ? `${v} ${u}` : `${v}`;
};

const safeName = (s: string): string =>
  s.replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '-').slice(0, 30);

// ── Styles ────────────────────────────────────────────────────────────────────

const s = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    fontSize: 9,
    color: '#1a1a1a',
    paddingTop: 44,
    paddingBottom: 56,
    paddingHorizontal: 40,
    lineHeight: 1.4,
  },

  // Header
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 20,
  },
  venueName: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 14,
    color: '#111',
    letterSpacing: 0.2,
    marginBottom: 2,
  },
  venueSubtitle: { fontSize: 7.5, color: '#999', letterSpacing: 0.8 },
  docTitle: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 18,
    color: '#111',
    letterSpacing: 1.5,
    textAlign: 'right',
  },
  docSubtitle: { fontSize: 7.5, color: '#666', textAlign: 'right', marginTop: 2 },

  // Rules
  ruleBold:  { borderBottomWidth: 2, borderBottomColor: '#111', borderBottomStyle: 'solid', marginBottom: 14 },
  ruleLight: { borderBottomWidth: 1, borderBottomColor: '#e0e0e0', borderBottomStyle: 'solid', marginBottom: 14 },

  // Meta row
  metaRow:   { flexDirection: 'row', marginBottom: 14, flexWrap: 'wrap' },
  metaItem:  { marginRight: 24, marginBottom: 4, minWidth: 80 },
  metaLabel: { fontSize: 6.5, color: '#999', textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 3 },
  metaValue: { fontFamily: 'Helvetica-Bold', fontSize: 9, color: '#111' },

  // Data limitation box
  limitBox:  {
    borderWidth: 1,
    borderColor: '#e6a817',
    borderStyle: 'solid',
    backgroundColor: '#fffbee',
    borderRadius: 3,
    paddingHorizontal: 10,
    paddingVertical: 7,
    marginBottom: 16,
  },
  limitTitle: { fontFamily: 'Helvetica-Bold', fontSize: 7.5, color: '#a06010', marginBottom: 3 },
  limitText:  { fontSize: 7.5, color: '#7a5010', lineHeight: 1.5 },

  // Summary stat boxes
  statRow:   { flexDirection: 'row', marginBottom: 16 },
  statBox:   {
    flex: 1,
    marginRight: 8,
    borderWidth: 1,
    borderColor: '#e0e0e0',
    borderStyle: 'solid',
    borderRadius: 3,
    paddingHorizontal: 10,
    paddingVertical: 8,
  },
  statBoxLast: { marginRight: 0 },
  statLabel: { fontSize: 6.5, color: '#999', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 4 },
  statValue: { fontFamily: 'Helvetica-Bold', fontSize: 14, color: '#111' },
  statNote:  { fontSize: 6.5, color: '#bbb', marginTop: 3 },

  statIn:    { color: '#1a7f37' },
  statOut:   { color: '#cf222e' },

  // Reconciliation note
  reconRow:  { flexDirection: 'row', alignItems: 'center', marginBottom: 14 },
  reconDot:  { width: 7, height: 7, borderRadius: 4, marginRight: 6 },
  reconText: { fontSize: 7.5, color: '#555' },

  // Section headings
  sectionHead: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 7,
    color: '#999',
    textTransform: 'uppercase',
    letterSpacing: 0.9,
    marginBottom: 6,
  },

  // Table
  table:        { marginBottom: 14 },
  tableHead:    {
    flexDirection: 'row',
    backgroundColor: '#111',
    paddingVertical: 6,
    paddingHorizontal: 6,
  },
  thCell:       { fontFamily: 'Helvetica-Bold', fontSize: 6, color: '#fff', textTransform: 'uppercase', letterSpacing: 0.5 },
  tableRow:     {
    flexDirection: 'row',
    paddingVertical: 6,
    paddingHorizontal: 6,
    borderBottomWidth: 1,
    borderBottomColor: '#eeeeee',
    borderBottomStyle: 'solid',
  },
  tableRowAlt:  { backgroundColor: '#f7f7f7' },
  tdText:       { fontSize: 7.5, color: '#222' },
  tdMuted:      { fontSize: 7.5, color: '#777' },
  tdGreen:      { fontSize: 7.5, color: '#1a7f37', fontFamily: 'Helvetica-Bold' },
  tdRed:        { fontSize: 7.5, color: '#cf222e', fontFamily: 'Helvetica-Bold' },
  tdSub:        { fontSize: 6, color: '#aaa', marginTop: 1 },

  // Stock table columns
  csItem:     { flex: 3.5 },
  csLot:      { flex: 1.2 },
  csLocation: { flex: 1.8 },
  csQty:      { flex: 0.7, textAlign: 'right' },
  csCost:     { flex: 1.2, textAlign: 'right' },

  // Movement table columns
  cmDate:     { flex: 1.2 },
  cmType:     { flex: 1.4 },
  cmItem:     { flex: 3.2 },
  cmLot:      { flex: 1.1 },
  cmLoc:      { flex: 1.5 },
  cmIn:       { flex: 0.7, textAlign: 'right' },
  cmOut:      { flex: 0.7, textAlign: 'right' },

  // Payable note
  payableBox: {
    borderLeftWidth: 2,
    borderLeftColor: '#ccc',
    borderLeftStyle: 'solid',
    paddingLeft: 8,
    paddingVertical: 5,
    marginBottom: 14,
  },
  payableTitle: { fontFamily: 'Helvetica-Bold', fontSize: 7, color: '#999', marginBottom: 3 },
  payableText:  { fontSize: 7.5, color: '#555' },

  // Truncation notice
  truncNote: { fontSize: 7, color: '#999', textAlign: 'center', paddingVertical: 6 },

  // Footer
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 40,
    right: 40,
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderTopWidth: 1,
    borderTopColor: '#e8e8e8',
    borderTopStyle: 'solid',
    paddingTop: 6,
  },
  footerLeft:  { fontSize: 6, color: '#bbb' },
  footerRight: { fontSize: 6, color: '#bbb', textAlign: 'right' },
});

// ── Document component ────────────────────────────────────────────────────────

export interface ConsignmentReportDocumentProps {
  report: ConsignmentReconciliationResponse;
  venueName: string;
}

export const ConsignmentReportDocument: React.FC<ConsignmentReportDocumentProps> = ({
  report,
  venueName,
}) => {
  const { header, openingStock, movementsInPeriod, closingStock, reconciliationCheck, payableEstimate } = report;

  return (
    <Document
      title={`Consignment Reconciliation — ${header.ownershipAccount.name}`}
      author={venueName}
    >
      <Page size="A4" orientation="portrait" style={s.page} wrap>

        {/* ── Page header ───────────────────────────────────────────── */}
        <View style={s.header}>
          <View>
            <Text style={s.venueName}>{venueName}</Text>
            <Text style={s.venueSubtitle}>Venue Operations</Text>
          </View>
          <View>
            <Text style={s.docTitle}>CONSIGNMENT</Text>
            <Text style={s.docTitle}>RECONCILIATION</Text>
            <Text style={s.docSubtitle}>Stock reconciliation report</Text>
          </View>
        </View>

        <View style={s.ruleBold} />

        {/* ── Metadata ──────────────────────────────────────────────── */}
        <View style={s.metaRow}>
          <View style={s.metaItem}>
            <Text style={s.metaLabel}>Consignor</Text>
            <Text style={s.metaValue}>{header.supplier?.name ?? '—'}</Text>
          </View>
          <View style={s.metaItem}>
            <Text style={s.metaLabel}>Account</Text>
            <Text style={s.metaValue}>{header.ownershipAccount.name}</Text>
          </View>
          <View style={s.metaItem}>
            <Text style={s.metaLabel}>Period From</Text>
            <Text style={s.metaValue}>{fmtDate(header.periodFrom)}</Text>
          </View>
          <View style={s.metaItem}>
            <Text style={s.metaLabel}>Period To</Text>
            <Text style={s.metaValue}>{fmtDate(header.periodTo)}</Text>
          </View>
          <View style={s.metaItem}>
            <Text style={s.metaLabel}>Generated</Text>
            <Text style={s.metaValue}>{fmtDateTime(header.generatedAt)}</Text>
          </View>
        </View>

        <View style={s.ruleLight} />

        {/* ── Data limitation notice ────────────────────────────────── */}
        <View style={s.limitBox}>
          <Text style={s.limitTitle}>DATA LIMITATION — RECONCILIATION ONLY</Text>
          <Text style={s.limitText}>
            POS sales are not yet attributed to consignment accounts until the POS FIFO phase is
            implemented. This document is a stock reconciliation report, not a payable settlement.
            Movement totals reflect goods receipts and manual movements only.
          </Text>
        </View>

        {/* ── Stock summary stats ───────────────────────────────────── */}
        <Text style={s.sectionHead}>Stock Summary</Text>
        <View style={s.statRow}>
          <View style={s.statBox}>
            <Text style={s.statLabel}>Opening Stock</Text>
            <Text style={s.statValue}>{openingStock.totalQuantity}</Text>
            <Text style={s.statNote}>units at period start</Text>
          </View>
          <View style={s.statBox}>
            <Text style={s.statLabel}>Stock In</Text>
            <Text style={[s.statValue, s.statIn]}>+{movementsInPeriod.totalIn}</Text>
            <Text style={s.statNote}>received in period</Text>
          </View>
          <View style={s.statBox}>
            <Text style={s.statLabel}>Stock Out</Text>
            <Text style={[s.statValue, s.statOut]}>{movementsInPeriod.totalOut}</Text>
            <Text style={s.statNote}>depleted in period</Text>
          </View>
          <View style={[s.statBox, s.statBoxLast]}>
            <Text style={s.statLabel}>Closing Stock</Text>
            <Text style={s.statValue}>{closingStock.totalQuantity}</Text>
            <Text style={s.statNote}>units at period end</Text>
          </View>
        </View>

        {/* ── Reconciliation check ──────────────────────────────────── */}
        <View style={s.reconRow}>
          <View style={[s.reconDot, { backgroundColor: reconciliationCheck.reconciled ? '#1a7f37' : '#cf222e' }]} />
          <Text style={s.reconText}>
            {reconciliationCheck.reconciled
              ? 'Reconciled — opening + movements = closing (no variance)'
              : `Not reconciled — variance: ${reconciliationCheck.variance} units (possible orphaned movements)`}
          </Text>
        </View>

        {/* ── Payable note ──────────────────────────────────────────── */}
        <View style={s.payableBox}>
          <Text style={s.payableTitle}>PAYABLE ESTIMATE</Text>
          <Text style={s.payableText}>
            Not available — {payableEstimate.reason}
            {payableEstimate.partialCostBasis != null
              ? `  |  Current stock cost basis (informational): ${payableEstimate.partialCostBasis.toFixed(2)}`
              : ''}
          </Text>
        </View>

        <View style={s.ruleLight} />

        {/* ── Closing stock table ───────────────────────────────────── */}
        <Text style={s.sectionHead}>Closing Stock Detail</Text>
        <View style={s.table}>
          <View style={s.tableHead} fixed>
            <Text style={[s.thCell, s.csItem]}>Item</Text>
            <Text style={[s.thCell, s.csLot]}>Lot</Text>
            <Text style={[s.thCell, s.csLocation]}>Location</Text>
            <Text style={[s.thCell, s.csQty]}>Qty</Text>
            <Text style={[s.thCell, s.csCost]}>Unit Cost</Text>
          </View>

          {closingStock.items.length === 0 ? (
            <View style={s.tableRow}>
              <Text style={[s.tdMuted, { flex: 1, textAlign: 'center' }]}>No stock on hand.</Text>
            </View>
          ) : (
            closingStock.items.map((item, idx) => (
              <View
                key={`${item.inventoryItemId}-${item.locationId}-${item.lotId ?? 'null'}`}
                style={[s.tableRow, idx % 2 === 1 ? s.tableRowAlt : {}]}
                wrap={false}
              >
                <View style={s.csItem}>
                  <Text style={s.tdText}>{item.displayName ?? '—'}</Text>
                  {item.sizeValue ? (
                    <Text style={s.tdSub}>{fmtSize(item.sizeValue, item.sizeUnit)}</Text>
                  ) : null}
                </View>
                <View style={s.csLot}>
                  <Text style={s.tdMuted}>{item.lotNumber ?? '—'}</Text>
                  {item.supplierBatchRef ? (
                    <Text style={s.tdSub}>{item.supplierBatchRef}</Text>
                  ) : null}
                </View>
                <Text style={[s.tdMuted, s.csLocation]}>{item.locationName ?? '—'}</Text>
                <Text style={[s.tdText, s.csQty]}>{item.quantity}</Text>
                <Text style={[s.tdMuted, s.csCost]}>
                  {item.unitCostAtReceipt != null ? item.unitCostAtReceipt.toFixed(2) : '—'}
                </Text>
              </View>
            ))
          )}
        </View>

        {/* ── Movements table ──────────────────────────────────────── */}
        {movementsInPeriod.rows.length > 0 ? (
          <>
            <View style={s.ruleLight} />
            <Text style={s.sectionHead}>Movements in Period</Text>

            {movementsInPeriod.truncated ? (
              <Text style={s.truncNote}>
                Showing first 1,000 of {movementsInPeriod.totalMovements.toLocaleString()} movements. Download Excel for full detail.
              </Text>
            ) : null}

            <View style={s.table}>
              <View style={s.tableHead} fixed>
                <Text style={[s.thCell, s.cmDate]}>Date</Text>
                <Text style={[s.thCell, s.cmType]}>Type</Text>
                <Text style={[s.thCell, s.cmItem]}>Item</Text>
                <Text style={[s.thCell, s.cmLot]}>Lot</Text>
                <Text style={[s.thCell, s.cmLoc]}>Location</Text>
                <Text style={[s.thCell, s.cmIn]}>In</Text>
                <Text style={[s.thCell, s.cmOut]}>Out</Text>
              </View>

              {movementsInPeriod.rows.map((m, idx) => (
                <View
                  key={String(m.movementId)}
                  style={[s.tableRow, idx % 2 === 1 ? s.tableRowAlt : {}]}
                  wrap={false}
                >
                  <Text style={[s.tdMuted, s.cmDate]}>{fmtDate(m.postedAt)}</Text>
                  <Text style={[s.tdText, s.cmType]}>{m.type}</Text>
                  <View style={s.cmItem}>
                    <Text style={s.tdText}>{m.inventoryItem.displayName ?? '—'}</Text>
                    {m.inventoryItem.sizeValue ? (
                      <Text style={s.tdSub}>{fmtSize(m.inventoryItem.sizeValue, m.inventoryItem.sizeUnit)}</Text>
                    ) : null}
                  </View>
                  <Text style={[s.tdMuted, s.cmLot]}>{m.lot?.lotNumber ?? '—'}</Text>
                  <Text style={[s.tdMuted, s.cmLoc]}>{m.location?.name ?? '—'}</Text>
                  <Text style={[m.quantity > 0 ? s.tdGreen : s.tdMuted, s.cmIn]}>
                    {m.quantity > 0 ? `+${m.quantity}` : ''}
                  </Text>
                  <Text style={[m.quantity < 0 ? s.tdRed : s.tdMuted, s.cmOut]}>
                    {m.quantity < 0 ? Math.abs(m.quantity) : ''}
                  </Text>
                </View>
              ))}
            </View>
          </>
        ) : (
          <Text style={s.truncNote}>No movements in this period.</Text>
        )}

        {/* ── Footer ────────────────────────────────────────────────── */}
        <View style={s.footer} fixed>
          <Text style={s.footerLeft}>
            Consignment Reconciliation — {header.ownershipAccount.name} · Nota Wine
          </Text>
          <Text
            style={s.footerRight}
            render={({ pageNumber, totalPages }: { pageNumber: number; totalPages: number }) =>
              `Page ${pageNumber} of ${totalPages}`
            }
          />
        </View>

      </Page>
    </Document>
  );
};

// ── Download helper ───────────────────────────────────────────────────────────

export async function downloadConsignmentReportPdf(
  report: ConsignmentReconciliationResponse,
  venueName: string,
): Promise<void> {
  const doc = <ConsignmentReportDocument report={report} venueName={venueName} />;
  const blob = await pdf(doc).toBlob();
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `Consignment-${safeName(report.header.ownershipAccount.name)}-${report.header.periodTo.slice(0, 10)}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
